import {
  DataProxy,
  MutationResult,
  useApolloClient,
  useMutation,
} from '@apollo/client';
import { ExecutionResult } from 'graphql';

import {
  CreateTimelineEntryInput,
  CreateTimelineEntryMutation,
  CreateTimelineEntryMutationVariables,
  ImageMediaInput,
  ListTimelineEntriesQuery,
  ListTimelineEntriesQueryVariables,
  MeasurementSystem,
  PageType,
  VideoMediaInput,
} from '../../../types/__generated__';
import { TimelineImage } from '../../components/Timeline/timelineTypes';
import { usePageMeta } from '../usePageMeta';
import mutation from './queries/createTimelineEntry.gql';
import query from './queries/listTimelineEntries.gql';
import { updateActivityTotals } from './updateActivityTotals';
import { useMeasurementSystem } from './useMeasurementSystem';

type CreateTimelineEntry = (
  entry: Pick<
    CreateTimelineEntryInput,
    'type' | 'pageId' | 'pageSlug' | 'message' | 'createdDate' | 'fitness'
  > & {
    media: TimelineImage[];
  },
) => Promise<ExecutionResult<CreateTimelineEntryMutation>>;

export function useCreateTimelineEntry(): [
  CreateTimelineEntry,
  MutationResult<CreateTimelineEntryMutation>,
] {
  const measurementSystem = useMeasurementSystem();
  const client = useApolloClient();
  const { pageType, pageSlug, isPreview } = usePageMeta();

  const [runMutation, mutationResult] = useMutation<
    CreateTimelineEntryMutation,
    CreateTimelineEntryMutationVariables & {
      measurementSystem?: MeasurementSystem;
    }
  >(mutation, {
    update(cache, { data }) {
      if (!data || !data.createTimelineEntry) {
        return;
      }

      const newTimelineEntry = {
        ...data.createTimelineEntry,
        refreshRequestedTime: null,
      };

      const result = readTimelineEntries(
        pageType,
        pageSlug,
        cache,
        measurementSystem,
      );

      const {
        previousPage,
        previousOwner,
        previousTimeline,
        previousPageInfo,
        previousTimelineNodes,
      } = result;

      const newTimelineData: ListTimelineEntriesQuery = {
        page: {
          ...previousPage,
          owner: previousOwner,
          timeline: {
            ...previousTimeline,
            pageInfo: previousPageInfo,
            nodes: [newTimelineEntry, ...previousTimelineNodes],
            totalCount: previousTimeline.totalCount + 1,
          },
        },
      };

      writeTimelineEntries(pageType, pageSlug, newTimelineData, cache);
      updateActivityTotals(client, pageType, pageSlug, isPreview);
    },
    context: {
      credentials: 'include',
    },
  });

  const createTimelineEntry: CreateTimelineEntry = entry => {
    return runMutation({
      variables: {
        input: {
          ...entry,
          media: entry?.media?.reduce<{
            imageMedia: ImageMediaInput[];
            videoMedia: VideoMediaInput[];
          }>(
            (acc, media) => {
              if (media.__typename === 'ImageMedia') {
                acc.imageMedia = [{ url: media.url }, ...acc.imageMedia];
              }
              return acc;
            },
            { imageMedia: [], videoMedia: [] },
          ),
        },
        measurementSystem: measurementSystem,
      },
    });
  };

  return [createTimelineEntry, mutationResult];
}

function writeTimelineEntries(
  pageType: string,
  pageSlug: string,
  data: ListTimelineEntriesQuery,
  cache: DataProxy,
) {
  cache.writeQuery<ListTimelineEntriesQuery, ListTimelineEntriesQueryVariables>(
    {
      query: query,
      data,
      variables: {
        type: pageType.toUpperCase() as PageType,
        slug: pageSlug,
      },
    },
  );
}

const getEmptyPage = (slug: string, type: string) => ({
  id: '',
  legacyId: '',
  type: type.toUpperCase() as PageType,
  slug,
  url: '',
  title: '',
});

function readTimelineEntries(
  pageType: string,
  pageSlug: string,
  cache: DataProxy,
  measurementSystem?: MeasurementSystem,
) {
  const result = cache.readQuery<
    ListTimelineEntriesQuery,
    ListTimelineEntriesQueryVariables
  >({
    query: query,
    variables: {
      type: pageType.toUpperCase() as PageType,
      slug: pageSlug,
      measurementSystem,
    },
  });

  const previousPage = result?.page ?? getEmptyPage(pageSlug, pageType);
  const previousOwner = result?.page?.owner;
  const previousTimeline = result?.page?.timeline ?? { totalCount: 0 };
  const previousTimelineNodes = result?.page?.timeline?.nodes ?? [];
  const previousPageInfo = result?.page?.timeline?.pageInfo ?? {
    startCursor: '',
    endCursor: '',
    hasNextPage: false,
    hasPreviousPage: false,
  };

  return {
    previousPage,
    previousOwner,
    previousTimeline,
    previousTimelineNodes,
    previousPageInfo,
  };
}
